
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from '@tanstack/react-query';
import { supabase } from "@/integrations/supabase/client";
import { formatPsychologistsData } from "@/utils/scheduleUtils";
import { useRealtimeAllProfilesUpdates } from '@/hooks/useRealtimeProfileUpdates';

export const usePsychologistDirectory = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedPsychologistForModal, setSelectedPsychologistForModal] = useState<any>(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  const { isConnected } = useRealtimeAllProfilesUpdates();

  const { data: psychologists = [], isLoading, error: queryError } = useQuery({
    queryKey: ['psychologists-directory'],
    queryFn: async () => {
      console.log('PsychologistDirectory - Fetching psychologists from database');
      const { data, error } = await supabase
        .from('psychologists')
        .select('*')
        .eq('is_approved', true)
        .eq('status', 'approved')
        .order('rating', { ascending: false });
      
      if (error) {
        console.error('PsychologistDirectory - Database error:', error);
        throw error;
      }
      
      console.log('PsychologistDirectory - Raw data from database:', data);
      const formattedData = formatPsychologistsData(data || []);
      
      // Add mock availability data for enhanced functionality
      const enhancedData = formattedData.map(psychologist => ({
        ...psychologist,
        isOnline: Math.random() > 0.7, // Mock online status
        hasAvailabilityToday: Math.random() > 0.5, // Mock today availability
        languages: psychologist.languages || ['Português'],
        total_reviews: Math.floor(Math.random() * 200) + 10, // Add total_reviews property
      }));
      
      console.log('PsychologistDirectory - Enhanced psychologists:', {
        count: enhancedData.length,
        realtimeConnected: isConnected
      });
      return enhancedData;
    },
  });

  const handleViewProfile = (psychologistId: string) => {
    console.log('PsychologistDirectory - opening modal for psychologist:', psychologistId);
    const psychologist = psychologists.find(p => p.id === psychologistId);
    if (psychologist) {
      setSelectedPsychologistForModal(psychologist);
      setIsProfileModalOpen(true);
    }
  };

  const handleViewFullProfile = (psychologistId: string) => {
    console.log('PsychologistDirectory - navigating to full profile:', psychologistId);
    navigate(`/psychologist-details/${psychologistId}`);
  };

  const handleScheduleNow = (psychologistId: string) => {
    console.log('PsychologistDirectory - scheduling with psychologist:', psychologistId);
    sessionStorage.setItem('selectedPsychologist', psychologistId);
    navigate(`/schedule/step2?psychologistId=${psychologistId}`);
  };

  const closeModal = () => {
    setIsProfileModalOpen(false);
    setSelectedPsychologistForModal(null);
  };

  return {
    psychologists,
    isLoading,
    queryError,
    selectedPsychologistForModal,
    isProfileModalOpen,
    handleViewProfile,
    handleViewFullProfile,
    handleScheduleNow,
    closeModal,
  };
};
